import { Divider, Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { PickPointLocation } from 'sections/LKSection/modules/OrderModule/types'
import { getOrderWorkConditionsStepKey } from 'sections/LKSection/modules/OrderModule/utils'
import { PickPointFilesForm } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/PickPointFilesForm'
import { TimetableForm } from 'sections/LKSection/shared/PickPointSharedModule/components/TimetableForm/TimetableForm'
import { Margin } from 'components/ui'
import { Button, Flex, Input, Text, TextArea, Title } from 'components/uiKit'
import { getPvzEditAreaRoute, getPvzEditTimetableRoute } from 'utils/helpers/routes/getRoutes'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { ALL_EXCEPT_NUMBERS_REGEXP } from 'shared/constants/regExp'
import { LKRoutesEnum } from 'shared/constants/routes'

import { PvzKeys } from '../../../data'
import { Pvz } from '../../../stores/entity/Pvz'
import { getPvzEditAreaPageKey, getPvzItemPageKey } from '../../../utils'
import { PvzFeaturesForm } from './PvzFeaturesForm/PvzFeaturesForm'
import { validatePvz } from './validate'

type PvzItemFormType = {
  description: string
  courierWay: string
  alarmSystem: string
  dressingRoomsCount: number
  racksCount: string
  cellsCount: string
  maxCellsCount: string
}

type PvzItemFormProps = {
  pvz: Pvz
}

const getKey = (key: string) => `${PvzKeys.editAreaPage}.${key}`

export const PvzItemForm = observer(({ pvz }: PvzItemFormProps) => {
  const { viewPvzUiStore: uiStore } = useStore()

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [loadingUpdate, setLoadingUpdate] = useState(false)

  const isOwner = uiStore.selectedPvz?.id ? uiStore.isOwner(uiStore.selectedPvz.id) : false
  const disabled = !isOwner || Boolean(pvz.blockTime)
  const isShoppingMall = pvz.floorLevel === PickPointLocation.shoppingMall

  useEffect(() => {
    if (uiStore.initialized) {
      form.setFieldValue('racksCount', pvz.racksCount)
      form.setFieldValue('cellsCount', pvz.cellsCount)
      form.setFieldValue('maxCellsCount', pvz.maxCellsCount)
      form.setFieldValue('description', pvz.description)
      form.setFieldValue('courierWay', pvz.courierWay)
      form.setFieldValue('alarmSystem', pvz.alarmSystem)
      form.setFieldValue('dressingRoomsCount', +(uiStore.dressingRoomsCount || 0))
    }
  }, [uiStore.initialized])

  const navigateTo = (page: 'editArea' | 'editTimetable') => {
    if (pvz.id) {
      navigate(page === 'editArea' ? getPvzEditAreaRoute(pvz.id) : getPvzEditTimetableRoute(pvz.id))
    }
  }

  const updateButtonHandler = async () => {
    if (validatePvz.validate(uiStore) && pvz.id) {
      setLoadingUpdate(true)

      if (await uiStore.updatePvz(pvz.id)) {
        navigate(LKRoutesEnum.pvz)
      }

      setLoadingUpdate(false)
    }
  }

  const getLocationKey = (key: string) => `${OrderKeys.locationStep}.${key}`
  const getWorkConditionsKey = (key: string) => `${OrderKeys.workConditionsStep}.${key}`

  return (
    <Form layout="vertical" form={form} requiredMark={false}>
      <Form.Item<PvzItemFormType>
        label={t(getLocationKey('Как найти ПВЗ'))}
        rules={[
          { required: true, message: t(getSharedKey('required')) },
          { min: 30, message: t(getSharedKey('minLength'), { count: 30 }) },
          { max: 200, message: t(getSharedKey('maxLength'), { count: 200 }) },
        ]}
        name={'description'}
      >
        <TextArea
          value={uiStore.description}
          onChange={(e) => uiStore.setDescription(e.target.value)}
          placeholder={t(getLocationKey('Нюансы расположения ПВЗ'))}
          disabled={disabled}
          maxLength={500}
          autoSize
        />
      </Form.Item>

      <Form.Item<PvzItemFormType>
        label={`${t(getLocationKey('Информация для курьеров'))}${
          isShoppingMall ? '' : ` (${t(getSharedKey('notRequired'))})`
        }`}
        tooltip={t(getLocationKey('courierWayTooltip'))}
        rules={[
          { required: isShoppingMall, message: t(getSharedKey('required')) },
          { max: 256, message: t(getSharedKey('maxLength'), { count: 256 }) },
        ]}
        name={'courierWay'}
      >
        <TextArea
          value={uiStore.courierWay}
          onChange={(e) => uiStore.setCourierWay(e.target.value)}
          placeholder={t(getLocationKey('Информация для курьера'))}
          disabled={disabled}
          maxLength={500}
          autoSize
        />
      </Form.Item>

      <Form.Item<PvzItemFormType>
        label={t(getLocationKey('Инструкция по работе'))}
        tooltip={t(getLocationKey('alarmSystemTooltip'))}
        rules={[{ max: 200, message: t(getSharedKey('maxLength'), { count: 200 }) }]}
        name={'alarmSystem'}
      >
        <TextArea
          value={uiStore.alarmSystem}
          onChange={(e) => uiStore.setAlarmSystem(e.target.value)}
          placeholder={t(getLocationKey('Инструкция'))}
          disabled={disabled}
          maxLength={200}
          autoSize
        />
      </Form.Item>

      <Form.Item<PvzItemFormType>
        label={t(getWorkConditionsKey('Количество примерочных'))}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
        name={'dressingRoomsCount'}
      >
        <Input
          value={uiStore.dressingRoomsCount ?? undefined}
          onChange={(e) => uiStore.setDressingRoomsCount(e.target.value)}
          disabled={disabled}
          placeholder="2"
          type="number"
          min={0}
        />
      </Form.Item>

      <Divider />

      <PvzFeaturesForm disabled={disabled} />

      <Divider />

      <Flex vertical gap={16}>
        <Flex vertical gap={8}>
          <Flex vertical>
            <Text strong>{t(getKey('Площадь ПВЗ'))}:</Text>
            <Text>{pvz.area}</Text>
          </Flex>

          <Flex vertical>
            <Text strong>{t(getOrderWorkConditionsStepKey('Площадь склада'))}:</Text>
            <Text>{pvz.warehouseArea}</Text>
          </Flex>
        </Flex>

        {!disabled && (
          <Button onClick={() => navigateTo('editArea')}>{t(getPvzItemPageKey('Изменить площадь'))}</Button>
        )}
      </Flex>

      <Divider />

      <Form.Item<PvzItemFormType>
        label={t(getLocationKey('Количество стеллажей'))}
        name={'racksCount'}
        normalize={(value) => {
          return value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
        }}
      >
        <Input
          value={uiStore.racksCount ?? undefined}
          onChange={(e) => uiStore.setRacksCount(e.target.value)}
          disabled={disabled}
          placeholder="2"
          min={0}
        />
      </Form.Item>
      <Form.Item<PvzItemFormType>
        label={t(getLocationKey('Количество ячеек'))}
        name={'cellsCount'}
        normalize={(value) => {
          return value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
        }}
      >
        <Input
          value={uiStore.cellsCount ?? undefined}
          onChange={(e) => uiStore.setCellsCount(e.target.value)}
          disabled={disabled}
          placeholder="2"
          min={0}
        />
      </Form.Item>
      <Form.Item<PvzItemFormType>
        label={t(getLocationKey('Максимальное количество ячеек'))}
        name={'maxCellsCount'}
        normalize={(value) => {
          return value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
        }}
      >
        <Input
          value={uiStore.maxCellsCount ?? undefined}
          onChange={(e) => uiStore.setMaxCellsCount(e.target.value)}
          disabled={disabled}
          placeholder="2"
          min={0}
        />
      </Form.Item>

      <Divider />

      <Flex vertical gap={16}>
        <Title level={5}>{t(getWorkConditionsKey('График работы ПВЗ'))}</Title>

        <TimetableForm
          uiStore={uiStore.timetable}
          pickPointType={pvz.typePoint}
          floorLevel={pvz.floorLevel}
          disabled
          hideInfo
        />

        {!disabled && (
          <Button onClick={() => navigateTo('editTimetable')}>{t(getPvzItemPageKey('Изменить график работы'))}</Button>
        )}
      </Flex>

      <Divider />

      <Margin mt={8}>
        <PickPointFilesForm
          uiStore={uiStore.fileList}
          entity={pvz}
          disabled={disabled}
          isActivation
          initialized={uiStore.initialized}
        />
      </Margin>

      {isOwner && (
        <Button
          htmlType="submit"
          disabled={uiStore.isNoChanges || uiStore.isSwitchPvzLoading}
          loading={loadingUpdate}
          onClick={updateButtonHandler}
        >
          {t(getPvzEditAreaPageKey('Обновить ПВЗ'))}
        </Button>
      )}
    </Form>
  )
})
